import React, { useRef, FormEvent, KeyboardEvent } from "react";
import styles from "./search.module.scss";
import { IconButton } from "../IconButton";
import { Button } from "../Button";
import { useTranslation } from "lib/i18n/use-translation";
import SearchIcon from "Assets/icon-search.svg";

const MIN_SEARCH_QUERY_LENGTH = 3;
const MAX_SEARCH_QUERY_LENGTH = 100;

export interface SearchProps {
  searchQuery: string;
  onSubmitSearchQuery: (value: string) => void;
  autoFocus?: boolean;
  placeholder?: string;
  searchOnType?: boolean;
  showClearButton?: boolean;
  onClearSearch?: () => void;
}

export const Search = ({
  searchQuery,
  onSubmitSearchQuery,
  autoFocus,
  placeholder,
  searchOnType = false,
  showClearButton = false,
  onClearSearch,
}: SearchProps) => {
  const { t } = useTranslation("components");
  const formRef = useRef<HTMLFormElement>(null);
  const typingTimeoutRef = useRef<any>(null);
  const doneTypingInterval = 500;

  const handleTyping = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!searchOnType) return;
    clearTimeout(typingTimeoutRef.current);
    const value = formRef.current?.search.value || "";
    if (!value.length || value.length >= MIN_SEARCH_QUERY_LENGTH) {
      typingTimeoutRef.current = setTimeout(() => {
        onSubmitSearchQuery(value);
      }, doneTypingInterval);
    }
  };

  const handleSubmitSearchQuery = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearTimeout(typingTimeoutRef.current);
    onSubmitSearchQuery(e.currentTarget.search.value);
  };

  return (
    <div className={styles["search-input-container"]}>
      <form
        ref={formRef}
        onSubmit={handleSubmitSearchQuery}
        className={styles["search-form"]}
      >
        <IconButton
          icon={<img src={SearchIcon} alt="" />}
          aria-label={t("components:search.submitA11y")}
          type="submit"
        />
        <input
          autoFocus={autoFocus}
          autoComplete="off"
          type="search"
          name="search"
          minLength={MIN_SEARCH_QUERY_LENGTH}
          maxLength={MAX_SEARCH_QUERY_LENGTH}
          placeholder={placeholder || t("components:search.placeholder")}
          defaultValue={searchQuery}
          onKeyUp={handleTyping}
          className={styles["search-input-field"]}
        />
        {showClearButton && searchQuery && (
          <Button
            variant="ghost"
            aria-label={t("components:search.clearA11y")}
            onClick={() => onClearSearch && onClearSearch()}
            className={styles["clear-search-button"]}
          >
            {t("components:search.clear")}
          </Button>
        )}
      </form>
    </div>
  );
};

export const useSearch = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const handleSubmitSearchQuery = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };
  return {
    searchQuery,
    onSubmitSearchQuery: handleSubmitSearchQuery,
  };
};
